import _ from 'lodash';
import moment from 'moment';
import pluralize from 'pluralize';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  ClientLink,
  DateTime,
  Hours,
  Icon,
  InternalClientTooltip,
  Percent,
  PeriodFilter,
  ProjectLink,
  Tooltip,
  ExportDropdown,
} from '~/components';
import SingleSelectFilter from '~/components/filters/SingleSelectFilter';
import { useApi, useConfirmation, useWorkspace } from '~/contexts';
import { useDocumentTitle, useSearchParams, useSearchParamsConfig } from '~/hooks';
import useClientFilters from '~/hooks/useClientFilters';
import useMemberFilters from '~/hooks/useMemberFilters';
import useProjectFilters from '~/hooks/useProjectFilters';
import { PageLoader } from '~/routes/public/pages';
import { colors, weights } from '~/styles';
import { dateFormats, intervalOptions, mimeTypes } from '~/utils';
import ClientFiltersBar from '~/components/filters/ClientFiltersBar';
import ClientFiltersGroup from '~/components/filters/ClientFiltersGroup';
import ExportDialogAsync from '../components/ExportDialogAsync';
import FilterButton from '~/components/filters/FilterButton';
import MemberFiltersBar from '~/components/filters/MemberFiltersBar';
import MemberFiltersGroup from '~/components/filters/MemberFiltersGroup';
import ProjectFiltersBar from '~/components/filters/ProjectFiltersBar';
import ProjectFiltersGroup from '~/components/filters/ProjectFiltersGroup';
import Report from '../components/Report';
import VarianceBillableTypeFilter from '../components/VarianceBillableTypeFilter';
import Table from '../components/table';
import ReportActions from '../ReportActions';

const ProjectInfo = styled.div`
  margin-left: 1rem;
  line-height: 1;
  flex: 1;

  small {
    font-size: 0.75rem;
    color: ${colors.grey40};
    display: flex;
    padding-top: 0.25rem;
  }
`;

const Member = styled.div`
  display: flex;
  align-items: center;
  font-weight: ${weights.bold};
`;

const ToggleSwitch = styled.div`
  cursor: pointer;
  width: 30px;
  font-size: 14px;
`;

const intervals = {
  get custom() {
    return intervalOptions.custom;
  },

  get past_30_days() {
    return {
      key: 'past_30_days',
      label: 'Past 30 days',
      start: moment().subtract(30, 'days').format(dateFormats.isoDate),
      end: moment().subtract(1, 'day').format(dateFormats.isoDate),
      unit: 'custom',
    };
  },

  get past_60_days() {
    return {
      key: 'past_60_days',
      label: 'Past 60 days',
      start: moment().subtract(60, 'days').format(dateFormats.isoDate),
      end: moment().subtract(1, 'day').format(dateFormats.isoDate),
      unit: 'custom',
    };
  },

  get past_90_days() {
    return {
      key: 'past_90_days',
      label: 'Past 90 days',
      start: moment().subtract(90, 'days').format(dateFormats.isoDate),
      end: moment().subtract(1, 'day').format(dateFormats.isoDate),
      unit: 'custom',
    };
  },

  get past_4_weeks() {
    return {
      key: 'past_4_weeks',
      label: 'Past 4 weeks',
      start: moment().subtract(4, 'weeks').startOf('isoWeek').format(dateFormats.isoDate),
      end: moment().subtract(1, 'week').endOf('isoWeek').format(dateFormats.isoDate),
      unit: 'week',
    };
  },

  get past_12_weeks() {
    return {
      key: 'past_12_weeks',
      label: 'Past 12 weeks',
      start: moment().subtract(12, 'weeks').startOf('isoWeek').format(dateFormats.isoDate),
      end: moment().subtract(1, 'weeks').endOf('isoWeek').format(dateFormats.isoDate),
      unit: 'week',
    };
  },

  get past_26_weeks() {
    return {
      key: 'past_26_weeks',
      label: 'Past 26 weeks',
      start: moment().subtract(26, 'weeks').startOf('isoWeek').format(dateFormats.isoDate),
      end: moment().subtract(1, 'weeks').endOf('isoWeek').format(dateFormats.isoDate),
      unit: 'week',
    };
  },

  get past_3_months() {
    return {
      key: 'past_3_months',
      label: 'Past 3 months',
      start: moment().subtract(3, 'months').startOf('month').format(dateFormats.isoDate),
      end: moment().subtract(1, 'month').endOf('month').format(dateFormats.isoDate),
      unit: 'month',
    };
  },

  get past_6_months() {
    return {
      key: 'past_6_months',
      label: 'Past 6 months',
      start: moment().subtract(6, 'months').startOf('month').format(dateFormats.isoDate),
      end: moment().subtract(1, 'month').endOf('month').format(dateFormats.isoDate),
      unit: 'month',
    };
  },

  get past_9_months() {
    return {
      key: 'past_9_months',
      label: 'Past 9 months',
      start: moment().subtract(9, 'months').startOf('month').format(dateFormats.isoDate),
      end: moment().subtract(1, 'month').endOf('month').format(dateFormats.isoDate),
      unit: 'month',
    };
  },

  get past_12_months() {
    return {
      key: 'past_12_months',
      label: 'Past 12 months',
      start: moment().subtract(12, 'months').startOf('month').format(dateFormats.isoDate),
      end: moment().subtract(1, 'month').endOf('month').format(dateFormats.isoDate),
      unit: 'month',
    };
  },
};

const intervalsByScope = {
  get day() {
    return [intervals.custom, intervals.past_30_days, intervals.past_60_days, intervals.past_90_days];
  },

  get week() {
    return [intervals.custom, intervals.past_4_weeks, intervals.past_12_weeks, intervals.past_26_weeks];
  },

  get month() {
    return [
      intervals.custom,
      intervals.past_3_months,
      intervals.past_6_months,
      intervals.past_9_months,
      intervals.past_12_months,
    ];
  },
};

export default function HoursVarianceByMemberAndProject({ report }) {
  useDocumentTitle(report.name);
  const { workspace } = useWorkspace();
  const api = useApi();

  const [query, setQuery] = useState({ report: null, status: 'loading' });

  const searchParamsConfig = useSearchParamsConfig();

  const clientFilters = useClientFilters();
  const projectFilters = useProjectFilters();
  const memberFilters = useMemberFilters({
    memberStatusId: { ...searchParamsConfig.memberStatusId, default: 'active' },
  });

  const [params, setParams] = useState({
    period: null,
    unit: '',

    varianceBillableTypes: [],

    ...memberFilters.filters,
    ...clientFilters.filters,
    ...projectFilters.filters,
  });

  const [searchParamsStatus, setSearchParamsStatus] = useState('pending');
  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        period: {
          ...searchParamsConfig.periodByUnit,
          default: intervals.past_6_months,
          intervals: (unit) => intervalsByScope[unit],
          defaults: {
            day: intervals.past_30_days,
            week: intervals.past_12_weeks,
            month: intervals.past_6_months,
          },
        },
        unit: { default: 'month', valid: ['day', 'week', 'month'] },
        varianceBillableTypes: searchParamsConfig.varianceBillableTypes,

        ...memberFilters.searchParamsConfig,
        ...clientFilters.searchParamsConfig,
        ...projectFilters.searchParamsConfig,
      }),
      [searchParamsConfig, clientFilters, projectFilters, memberFilters],
    ),
    onChange: (params) => setParams((state) => ({ ...state, ...params })),
  });

  // Map the values to perform the API query
  const urlSearchParams = useMemo(
    () => ({
      start: params.period?.start ?? undefined,
      end: params.period?.end ?? undefined,
      unit: params.unit,
      varianceBillableTypeId: params.varianceBillableTypes?.map((v) => v.id),

      ...memberFilters.mapUrlSearchParams(params),
      ...clientFilters.mapUrlSearchParams(params),
      ...projectFilters.mapUrlSearchParams(params),
    }),
    [params, clientFilters, projectFilters, memberFilters],
  );

  useEffect(() => {
    if (searchParamsStatus === 'ready') return;
    searchParams.get().then((params) => {
      setParams((state) => ({ ...state, ...params }));
      setSearchParamsStatus('ready');
    });
  }, [searchParams, searchParamsStatus]);

  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).reports(report.id).data(urlSearchParams);
    setQuery((state) => ({ ...state, data, status: 'ready' }));
  }, [api, workspace.id, report.id, urlSearchParams]);

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    fetchData();
  }, [fetchData, searchParamsStatus]);

  const [filtersVisible, setFiltersVisible] = useState(false);
  const showFilters = () => setFiltersVisible(true);
  const hideFilters = () => setFiltersVisible(false);
  const handleApplyFilters = (values) => {
    if (values !== params) setQuery((state) => ({ ...state, status: 'filtering' }));

    setParams({ ...params, ...values });
    searchParams.set(values);
    hideFilters();
  };

  const confirmation = useConfirmation();

  const handleExport = (mimeType) => {
    confirmation.prompt((resolve) => (
      <ExportDialogAsync
        onLoad={api.www
          .workspaces(workspace.id)
          .reports(report.id)
          .data(urlSearchParams, {
            headers: { accept: mimeType },
          })}
        onClose={resolve}
      />
    ));
  };

  return (
    <Report>
      <Report.Header>
        <Report.Info report={report} />

        <Report.Actions>
          <ReportActions onSave={() => searchParams.set(params)} />

          <ExportDropdown>
            {({ setIsOpen }) => (
              <>
                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.csv);
                    setIsOpen(false);
                  }}>
                  Export to CSV
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.xlsx);
                    setIsOpen(false);
                  }}>
                  Export to Excel
                </ExportDropdown.Item>
              </>
            )}
          </ExportDropdown>

          <FilterButton isOutline onClick={showFilters} />
        </Report.Actions>
      </Report.Header>

      <Report.FiltersBar>
        <PeriodFilter
          clearable={false}
          scope={params.unit}
          intervals={intervalsByScope[params.unit]}
          placeholder="Date Range"
          value={params.period}
          onChange={({ target: { value } }) => handleApplyFilters({ period: value })}
        />

        <UnitFilter
          value={params.unit}
          onChange={({ target: { value } }) =>
            handleApplyFilters({
              unit: value,
              period: {
                day: intervals.past_30_days,
                week: intervals.past_12_weeks,
                month: intervals.past_6_months,
              }[value],
            })
          }
        />

        <ClientFiltersBar filters={params} onChange={handleApplyFilters} />

        <ProjectFiltersBar filters={params} onChange={handleApplyFilters} />

        <MemberFiltersBar filters={params} onChange={handleApplyFilters} />

        {!_.isEmpty(params.varianceBillableTypes) && (
          <VarianceBillableTypeFilter
            value={params.varianceBillableTypes}
            onChange={({ target: { value } }) => handleApplyFilters({ varianceBillableTypes: value })}
          />
        )}
      </Report.FiltersBar>

      {(() => {
        switch (query.status) {
          case 'loading':
          case 'filtering':
            return <PageLoader />;

          default:
            return (
              <>
                <Data query={query} params={params} />
                <Filters values={params} isOpen={filtersVisible} onApply={handleApplyFilters} onClose={hideFilters} />
              </>
            );
        }
      })()}
    </Report>
  );
}

function Data({ query, params }) {
  const report = query.data;
  const [expandedIds, setExpandedIds] = useState([]);

  const handleToggle = (memberId) => {
    setExpandedIds((prevIds) =>
      prevIds.includes(memberId) ? prevIds.filter((id) => id !== memberId) : [...prevIds, memberId],
    );
  };

  const handleToggleAll = () => {
    setExpandedIds((prevIds) => {
      if (prevIds.length) {
        return [];
      }

      return report.members.map((member) => member.id);
    });
  };

  const { members, projectsById, records, periods } = useMemo(() => {
    const members = _.orderBy(report.members, (r) => r.name.toLowerCase());

    const projectsById = _.keyBy(report.projects, 'id');

    const records = _.orderBy(report.records, (a) => projectsById[a.projectId].name.toLowerCase()).reduce(
      (a, v) => {
        a.total.worked += v.worked || 0;
        a.total.planned += v.planned || 0;

        // Members
        a.members[v.memberId] = a.members[v.memberId] || {
          total: {
            worked: 0,
            planned: 0,
            get variance() {
              return this.worked - this.planned;
            },
            get variancePercentage() {
              if (!this.planned) return null;
              return this.variance / this.planned;
            },
          },
          periods: {},
          projects: {},
        };
        a.members[v.memberId].total.worked += v.worked || 0;
        a.members[v.memberId].total.planned += v.planned || 0;
        if (!a.members[v.memberId].periods[v.start])
          a.members[v.memberId].periods[v.start] = {
            worked: 0,
            planned: 0,
            get variance() {
              return this.worked - this.planned;
            },
            get variancePercentage() {
              if (!this.planned) return null;
              return this.variance / this.planned;
            },
          };
        a.members[v.memberId].periods[v.start].worked += v.worked || 0;
        a.members[v.memberId].periods[v.start].planned += v.planned || 0;
        a.members[v.memberId].projects[v.projectId] = a.members[v.memberId].projects[v.projectId] || {
          total: {
            worked: 0,
            planned: 0,
            get variance() {
              return this.worked - this.planned;
            },
            get variancePercentage() {
              if (!this.planned) return null;
              return this.variance / this.planned;
            },
          },
          periods: {},
        };

        // Projects
        a.members[v.memberId].projects[v.projectId].total.worked += v.worked || 0;
        a.members[v.memberId].projects[v.projectId].total.planned += v.planned || 0;
        a.members[v.memberId].projects[v.projectId].periods[v.start] = {
          worked: v.worked,
          planned: v.planned,
          variance: v.variance,
          get variancePercentage() {
            if (!this.planned) return null;
            return this.variance / this.planned;
          },
        };

        // Periods
        if (!a.periods[v.start])
          a.periods[v.start] = {
            worked: 0,
            planned: 0,
            get variance() {
              return this.worked - this.planned;
            },
            get variancePercentage() {
              if (!this.planned) return null;
              return this.variance / this.planned;
            },
          };
        a.periods[v.start].worked += v.worked || 0;
        a.periods[v.start].planned += v.planned || 0;

        return a;
      },
      {
        members: {},
        periods: {},
        total: {
          worked: 0,
          planned: 0,
          get variance() {
            return this.worked - this.planned;
          },
          get variancePercentage() {
            if (!this.planned) return null;
            return this.variance / this.planned;
          },
        },
      },
    );

    let start = params.period.start;
    let end = params.period.end;

    switch (params.unit) {
      case 'day':
        start = moment(start).format(dateFormats.isoDate);
        end = moment(end).format(dateFormats.isoDate);
        break;

      case 'week':
        start = moment(start).startOf('isoWeek').format(dateFormats.isoDate);
        end = moment(end).endOf('isoWeek').format(dateFormats.isoDate);
        break;

      case 'month':
        start = moment(start).startOf('month').format(dateFormats.isoDate);
        end = moment(end).endOf('month').format(dateFormats.isoDate);
        break;
    }

    const periodCount = moment(end).diff(start, params.unit) + 1;
    const periods = [];
    for (let index = 0; index < periodCount; index++) {
      periods.push(moment(start).add(index, params.unit).format(dateFormats.isoDate));
    }

    return { members, projectsById, records, periods };
  }, [report, params.period.start, params.period.end, params.unit]);

  return (
    <Report.Table>
      <Table>
        <Table.Header>
          <Table.Column sticky minWidth="16rem">
            <ToggleSwitch
              onClick={() => {
                handleToggleAll();
              }}>
              <Icon color={colors.grey25} icon={expandedIds.length ? 'chevron-down' : 'chevron-right'} />
            </ToggleSwitch>
            Member
          </Table.Column>

          {periods.map((p) => (
            <Table.Column key={p} align="right" width="7rem">
              <DateTime value={p} />
            </Table.Column>
          ))}

          <Table.Column align="right" width="7rem">
            Total
          </Table.Column>
        </Table.Header>

        <Table.Body>
          {members.map((member) => {
            return (
              <React.Fragment key={member.id}>
                <Table.GroupRow top="3rem">
                  <Table.Cell>
                    <ToggleSwitch
                      onClick={() => {
                        handleToggle(member.id);
                      }}>
                      <Icon
                        color={colors.grey25}
                        icon={expandedIds.includes(member.id) ? 'chevron-down' : 'chevron-right'}
                      />
                    </ToggleSwitch>
                    <Member>{member.name}</Member>
                  </Table.Cell>

                  {periods.map((period) => {
                    const value = records.members[member.id]?.periods[period];

                    return (
                      <Table.Cell key={period}>
                        <CellValue value={value} />
                      </Table.Cell>
                    );
                  })}

                  <Table.Cell>
                    <CellValue value={records.members[member.id]?.total} />
                  </Table.Cell>
                </Table.GroupRow>

                {expandedIds.includes(member.id) &&
                  _.map(records.members[member.id]?.projects, (projectRecords, key) => {
                    const project = projectsById[key];

                    return (
                      <Table.Row key={key}>
                        <Table.Cell>
                          <ProjectInfo>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              {project.permissions.view ? <ProjectLink project={project} /> : project.name}
                            </div>
                            <small>
                              {project.client.permissions.view ? (
                                <ClientLink client={project.client} />
                              ) : (
                                project.client.name
                              )}
                              {project.client.isInternal && <InternalClientTooltip />}
                            </small>
                          </ProjectInfo>
                          {!project.permissions.view && (
                            <Tooltip message="You are not currently assigned to this project.">
                              <Icon icon="info-circle" color={colors.grey25} spaceLeft />
                            </Tooltip>
                          )}
                        </Table.Cell>

                        {periods.map((period) => {
                          const value = projectRecords.periods[period];

                          return (
                            <Table.Cell key={period}>
                              <CellValue value={value} />
                            </Table.Cell>
                          );
                        })}

                        <Table.Cell>
                          <CellValue value={projectRecords.total} />
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
              </React.Fragment>
            );
          })}

          <Table.Row style={{ fontWeight: weights.bold }}>
            <Table.Cell>Total</Table.Cell>

            {periods.map((period) => {
              const value = records.periods[period];

              return (
                <Table.Cell key={period}>
                  <CellValue value={value} />
                </Table.Cell>
              );
            })}

            <Table.Cell>
              <CellValue value={records.total} />
            </Table.Cell>
          </Table.Row>
        </Table.Body>

        <Table.Status
          total={members.length}
          label="Member"
          isLoading={query.status !== 'ready'}
          partial={report.partialResults}
        />
      </Table>
    </Report.Table>
  );
}

function Filters({ values, isOpen, onClose, onApply }) {
  const [filters, setFilters] = useState(values);

  const handleApply = () => {
    onApply(filters);
  };

  const handleFilter = (filter) => {
    setFilters({ ...filters, ...filter });
  };

  const handleCancel = () => {
    setFilters(values);
    onClose();
  };

  useEffect(() => {
    setFilters(values);
  }, [values]);

  return (
    <Report.FiltersDrawer isOpen={isOpen} onApply={handleApply} onClose={handleCancel}>
      <UnitFilter
        value={filters.unit}
        onChange={({ target: { value } }) =>
          handleFilter({
            unit: value,
            period: {
              day: intervals.past_30_days,
              week: intervals.past_4_weeks,
              month: intervals.past_6_months,
            }[value],
          })
        }
      />

      <PeriodFilter
        clearable={false}
        scope={filters.unit}
        intervals={intervalsByScope[filters.unit]}
        placeholder="Date Range"
        value={filters.period}
        onChange={({ target: { value } }) => handleFilter({ period: value })}
      />

      <VarianceBillableTypeFilter
        value={filters.varianceBillableTypes}
        onChange={({ target: { value } }) => handleFilter({ varianceBillableTypes: value })}
      />

      <ClientFiltersGroup filters={filters} onChange={handleFilter} />

      <ProjectFiltersGroup filters={filters} onChange={handleFilter} />

      <MemberFiltersGroup filters={filters} onChange={handleFilter} />
    </Report.FiltersDrawer>
  );
}

const TooltipContainer = styled.div`
  min-width: 14rem;
  padding: 0.75rem 0;
`;

const TooltipInfo = styled.div`
  display: flex;
  font-size: 0.75rem;

  &:not(:last-child) {
    margin-bottom: 0.75rem;
  }
`;

const TooltipLabel = styled.div`
  width: 7rem;
  padding-right: 0.5rem;
  color: ${colors.grey40};
  font-weight: ${weights.black};
  letter-spacing: 0.0625rem;
  text-transform: uppercase;
  flex-shrink: 0;
  white-space: wrap;
`;

const TooltipValue = styled.div`
  white-space: wrap;
`;

function TooltipHours({ value }) {
  return (
    <>
      <Hours value={value} /> {_.isNumber(value) && pluralize('hours', value)}
    </>
  );
}

function CellValue({ value }) {
  if (!value) return <Hours value={null} />;

  return (
    <Tooltip
      message={
        <TooltipContainer>
          <TooltipInfo>
            <TooltipLabel>Actual</TooltipLabel>
            <TooltipValue>
              <TooltipHours value={value.worked} />
            </TooltipValue>
          </TooltipInfo>

          <TooltipInfo>
            <TooltipLabel>Planned</TooltipLabel>
            <TooltipHours value={value.planned} />
          </TooltipInfo>

          <TooltipInfo>
            <TooltipLabel>Variance</TooltipLabel>
            <TooltipValue style={{ color: value.variance < 0 ? colors.danger : colors.black }}>
              <TooltipHours value={value.variance} />
            </TooltipValue>
          </TooltipInfo>

          <TooltipInfo>
            <TooltipLabel>Variance %</TooltipLabel>
            <TooltipValue style={{ color: value.variancePercentage < 0 ? colors.danger : colors.black }}>
              <Percent value={value.variancePercentage} />
            </TooltipValue>
          </TooltipInfo>
        </TooltipContainer>
      }>
      <span style={{ color: value.variance < 0 ? colors.danger : colors.black }}>
        <Hours value={value.variance} />
      </span>
    </Tooltip>
  );
}

function UnitFilter(props) {
  return (
    <SingleSelectFilter
      icon="gear"
      placeholder="Unit"
      clearable={false}
      renderValue={(value) => value.name}
      options={[
        { id: 'month', name: 'Month' },
        { id: 'week', name: 'Week' },
        { id: 'day', name: 'Day' },
      ]}
      {...props}
    />
  );
}
